import React, { useState } from 'react';
import axios from 'axios';
import { Mail, Send, CheckCircle2, Clock, ShieldCheck } from 'lucide-react';
import { siteInfo, pricingPlans } from '../mock';
import Seo from '../components/Seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export default function Contact() {
  const [form, setForm] = useState({ name: '', email: '', plan: '', message: '', website: '' });
  const [sent, setSent] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      setError('Please fill in your name, email and message.');
      return;
    }
    if (!EMAIL_RE.test(form.email.trim())) {
      setError('Please enter a valid email address.');
      return;
    }
    if (form.message.trim().length < 5) {
      setError('Please enter a longer message.');
      return;
    }
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        plan: form.plan,
        message: form.message.trim(),
        website: form.website, // honeypot (hidden)
      });
      setSent(true);
      setForm({ name: '', email: '', plan: '', message: '', website: '' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setError(typeof detail === 'string' ? detail : 'Something went wrong. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Seo
        title="Contact 24BetBox | Subscribe to VIP Football Tips"
        description="Get in touch with 24BetBox to subscribe to our VIP football predictions or ask a question. Our team replies to all enquiries within hours, 24/7."
        path="/contact"
      />
      <section className="hero-gradient border-b border-[#2e2822]">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 py-16">
          <p className="font-heading text-sm font-bold uppercase tracking-[0.2em] text-orange-500">Get In Touch</p>
          <h1 className="mt-2 font-heading text-5xl font-extrabold text-white sm:text-6xl">Contact Us</h1>
          <p className="mt-4 max-w-2xl text-neutral-300">Ready to subscribe or have a question? Send us a message and our team will reply with everything you need to get started.</p>
        </div>
      </section>

      <section className="bg-[#15120f] py-16">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="grid gap-10 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <div className="card-dark rounded-2xl p-8">
                {sent ? (
                  <div className="flex flex-col items-center py-10 text-center">
                    <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-500/15"><CheckCircle2 className="h-8 w-8 text-emerald-400" /></div>
                    <h3 className="mt-4 font-heading text-2xl font-extrabold text-white">Message Sent!</h3>
                    <p className="mt-2 max-w-md text-neutral-400">Thank you for reaching out. Our team will get back to you shortly at the email you provided.</p>
                    <button onClick={() => setSent(false)} className="btn-orange mt-6 rounded-lg px-6 py-3 font-heading text-sm font-bold uppercase">Send Another Message</button>
                  </div>
                ) : (
                  <form onSubmit={onSubmit} className="space-y-5">
                    <h3 className="font-heading text-2xl font-extrabold text-white">Send Us A Message</h3>
                    <div className="grid gap-5 sm:grid-cols-2">
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-neutral-300">Your Name</label>
                        <input name="name" value={form.name} onChange={onChange} placeholder="John Doe" className="w-full rounded-lg border border-[#2e2822] bg-[#1b1712] px-4 py-3 text-sm text-white placeholder-neutral-500 outline-none focus:border-orange-500/60" />
                      </div>
                      <div>
                        <label className="mb-1.5 block text-sm font-medium text-neutral-300">Email Address</label>
                        <input name="email" type="email" value={form.email} onChange={onChange} placeholder="you@example.com" className="w-full rounded-lg border border-[#2e2822] bg-[#1b1712] px-4 py-3 text-sm text-white placeholder-neutral-500 outline-none focus:border-orange-500/60" />
                      </div>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-neutral-300">Interested Plan</label>
                      <select name="plan" value={form.plan} onChange={onChange} className="w-full rounded-lg border border-[#2e2822] bg-[#1b1712] px-4 py-3 text-sm text-white outline-none focus:border-orange-500/60">
                        <option value="">Select a plan (optional)</option>
                        {pricingPlans.map((p) => (<option key={p.id} value={p.name}>{p.name} — €{p.price}</option>))}
                        <option value="VIP Ticket">VIP Ticket</option>
                        <option value="General Enquiry">General Enquiry</option>
                      </select>
                    </div>
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-neutral-300">Message</label>
                      <textarea name="message" value={form.message} onChange={onChange} rows={5} placeholder="Tell us how we can help..." className="w-full rounded-lg border border-[#2e2822] bg-[#1b1712] px-4 py-3 text-sm text-white placeholder-neutral-500 outline-none focus:border-orange-500/60" />
                    </div>
                    {/* Honeypot field - hidden from humans, catches bots */}
                    <input
                      type="text"
                      name="website"
                      value={form.website}
                      onChange={onChange}
                      tabIndex={-1}
                      autoComplete="off"
                      aria-hidden="true"
                      style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
                    />
                    {error && <p className="text-sm text-red-400">{error}</p>}
                    <button type="submit" disabled={loading} className="btn-orange inline-flex w-full items-center justify-center gap-2 rounded-lg py-3.5 font-heading text-base font-bold uppercase tracking-wide disabled:opacity-60 sm:w-auto sm:px-8"><Send className="h-4 w-4" /> {loading ? 'Sending...' : 'Send Message'}</button>
                  </form>
                )}
              </div>
            </div>
            <div className="space-y-5">
              <div className="card-dark rounded-2xl p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><Mail className="h-6 w-6 text-orange-500" /></div>
                <h4 className="mt-4 font-heading text-lg font-bold text-white">Email Us</h4>
                <a href={`mailto:${siteInfo.email}`} className="mt-1 block text-sm text-orange-400 hover:text-orange-300 break-all">{siteInfo.email}</a>
              </div>
              <div className="card-dark rounded-2xl p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><Clock className="h-6 w-6 text-orange-500" /></div>
                <h4 className="mt-4 font-heading text-lg font-bold text-white">Response Time</h4>
                <p className="mt-1 text-sm text-neutral-400">We reply to all enquiries within a few hours, 24/7.</p>
              </div>
              <div className="card-dark rounded-2xl p-6">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500/15"><ShieldCheck className="h-6 w-6 text-orange-500" /></div>
                <h4 className="mt-4 font-heading text-lg font-bold text-white">Secure & Private</h4>
                <p className="mt-1 text-sm text-neutral-400">Your details are kept strictly confidential and never shared.</p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
